import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import loadable from '@loadable/component';

import { trackProduct } from '@jsmdg/tracking';
import { Breakpoint, useBreakpoint } from '@jsmdg/yoshi';
import { isBox } from '../../../shared/product-safeguards';
import { type Reviews } from '../../../shared/reviews';
import { type Product } from '../../../shared/types';
import { useProduct } from '../../hooks/useProduct';
import { useReviews } from '../../hooks/useReviews';
import { AccordionContainer } from '../AccordionContainer';
import { ErrorBoundary } from '../ErrorBoundary';
import { GiftBoxRelatedProductsFallback } from '../GiftBoxRelatedProducts/GiftBoxRelatedProductsFallback';
import { StrikePriceInfo } from '../Price/StrikePriceInfo';
import { ProductMarkup } from '../ProductMarkup/ProductMarkup';
import { ProductServiceInfo } from '../ProductServiceInfo/ProductServiceInfo';
import { ServiceConsultant } from '../ServiceConsultant';
import { useProductInfoAccordions } from './ProductInfoContentBuilder';
import { USPSliderMobile } from './USPSliderMobile';

const LoadableGiftBoxRelatedProducts = loadable(
    async () => import('../GiftBoxRelatedProducts/GiftBoxRelatedProducts'),
    {
        fallback: <GiftBoxRelatedProductsFallback />,
    },
);

type Props = {
    readonly numTickets: number;
    readonly boxClusterAreaRef: React.RefObject<HTMLHeadingElement>;
};

const ProductInfo = ({ boxClusterAreaRef, numTickets }: Props): JSX.Element => {
    const product = useProduct();
    const { areReviewsInitiallyFetched, reviews } = useReviews();

    const productMarkupData: Product & Reviews = {
        ...product,
        ...((reviews || {}) as Reviews),
    };
    const canRenderProductMarkup = areReviewsInitiallyFetched && typeof window !== 'undefined';
    const isMobile = !useBreakpoint(Breakpoint.SM);
    const accordions = useProductInfoAccordions(product);

    useEffect(() => {
        trackProduct(product, window.location.origin);
    }, [product]);

    return (
        <>
            <ProductServiceInfo content={product.productInformation.services} />
            {isMobile && <StrikePriceInfo numTickets={numTickets} />}
            <AccordionContainer product={product} accordions={accordions} isMobile={isMobile} />

            {isBox(product) && !product.voucherPartnerInformation.iframeUrl && (
                <ErrorBoundary>
                    <LoadableGiftBoxRelatedProducts
                        productId={product.productId}
                        inPreview={product.inPreview}
                        boxClusterAreaRef={boxClusterAreaRef}
                        isCluster={!!product.minPrice}
                    />
                </ErrorBoundary>
            )}

            <ServiceConsultant brand={product.brand} />

            <div className="pt-3x theme-text-caption-text">
                <FormattedMessage defaultMessage="Artikelnummer" />: {product.manufacturerSku}
            </div>
            {isMobile && <USPSliderMobile />}

            {canRenderProductMarkup && <ProductMarkup markupData={productMarkupData} />}
        </>
    );
};

export { ProductInfo };
